import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Loader2, FileSpreadsheet, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { HltReport } from './HltReport';
import { HltSettings } from './HltSettings';
import { HltStatusBar } from './HltStatusBar';
import { HltCompare } from './HltCompare';

const istMonth = () => new Date(Date.now() + 5.5 * 3600e3).toISOString().slice(0, 7);

export default function HotTransferPage() {
  const [month, setMonth] = useState(istMonth());
  const [tab, setTab] = useState('report');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const latest = useRef(month);
  const load = useCallback(() => {
    latest.current = month;
    setLoading(true);
    return api.get('/hot-transfer/report', { params: { month } }).then(r => { if (latest.current === month) setData(r.data); })
      .catch(e => toast.error(e.response?.data?.detail || 'Could not load')).finally(() => setLoading(false));
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const refresh = useCallback(async (quiet) => {
    const m = month;
    setRefreshing(true);
    try { const r = await api.post('/hot-transfer/refresh', { month: m }); if (latest.current === m) setData(r.data); if (!quiet) toast.success('Updated from Zoho CRM'); }
    catch (e) { toast.error(e.response?.data?.detail || 'CRM read failed'); } finally { setRefreshing(false); }
  }, [month]);
  useEffect(() => {
    if (data && data.month === month && !data.refreshed_at && data.status === 'provisional') refresh(true);
  }, [data, month, refresh]);
  const prevMonth = (() => { const [y, m] = month.split('-').map(Number); const d = new Date(y, m - 2, 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; })();
  const exportXlsx = async () => {
    try {
      const r = await api.get('/hot-transfer/export', { params: { month }, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `hot-lead-transfer-${month}.xlsx`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch { toast.error('Export failed'); }
  };
  const locked = data && data.status !== 'provisional';

  return (
    <div className="space-y-5" data-testid="hot-transfer-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Hot Lead Transfer Incentive</h1>
          <p className="text-sm text-slate-500 mt-0.5">₹{data?.settings?.rate ?? '…'} per Hot lead · credited by Inbound Sales Person Emp ID · lead must be Suspect or Call Back before Hot · month = date it turned Hot · this month auto-updates nightly at 11:59 PM IST</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => refresh()} disabled={refreshing || locked} data-testid="hlt-refresh-btn">
            {refreshing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}Update from CRM</Button>
          <Button size="sm" variant="outline" onClick={exportXlsx} disabled={!data?.refreshed_at} data-testid="hlt-export-btn"><FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" />Export Excel</Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <input type="month" value={month} max={istMonth()} onChange={e => e.target.value && setMonth(e.target.value)} className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white" data-testid="hlt-month-input" />
        <Tabs value={tab} onValueChange={setTab} className="ml-auto">
          <TabsList><TabsTrigger value="report" data-testid="hlt-tab-report">Report</TabsTrigger>
            <TabsTrigger value="compare" data-testid="hlt-tab-compare">Month comparison</TabsTrigger>
            <TabsTrigger value="settings" data-testid="hlt-tab-settings">Settings & Emp ID mapping</TabsTrigger></TabsList>
        </Tabs>
      </div>

      {loading && !data && <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="w-6 h-6 animate-spin mr-2" />Loading…</div>}
      {data && tab === 'report' && (
        <>
          <HltStatusBar data={data} onChanged={setData} />
          {refreshing && !data.refreshed_at && <p className="text-xs text-blue-700 flex items-center gap-2" data-testid="hlt-auto-loading"><Loader2 className="w-3.5 h-3.5 animate-spin" />Reading this month from Zoho CRM for the first time…</p>}
          {data.refreshed_at && data.totals.hot === 0 && (
            <div className="text-xs bg-blue-50 border border-blue-200 text-blue-900 rounded-md px-3 py-2 flex items-center gap-3" data-testid="hlt-empty-hint">
              No qualifying Hot transfers in this month yet (counting starts {data.settings?.start_date}; Akriti and Aman are excluded).
              {prevMonth >= (data.settings?.start_date || '').slice(0, 7) && <Button size="sm" variant="outline" className="h-7 ml-auto" onClick={() => setMonth(prevMonth)} data-testid="hlt-view-prev-month-btn">View previous month</Button>}
            </div>
          )}
          <HltReport data={data} onSettings={() => setTab('settings')} />
        </>
      )}
      {tab === 'compare' && <HltCompare month={month} />}
      {tab === 'settings' && <HltSettings onChanged={load} />}
    </div>
  );
}
